import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../api/client";
import { newClientOfflineId, enqueue } from "../../lib/offlineQueue";
import { useOffline } from "../../contexts/OfflineContext";
import { ArrowLeft, Search, AlertTriangle, Play } from "lucide-react";
import { MOBILE } from "../../constants/testIds";
import { toast } from "sonner";

const WORK_TYPES = ["FW", "OW", "drafting", "search", "travel"];

export default function AddJob() {
    const nav = useNavigate();
    const { online, refreshCount } = useOffline();
    const [tab, setTab] = useState("existing"); // "existing" | "new"
    const [q, setQ] = useState("");
    const [jobs, setJobs] = useState([]);
    const [addr, setAddr] = useState("");
    const [reason, setReason] = useState("");
    const [workType, setWorkType] = useState("FW");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/jobs", { params: q ? { q } : {} }).then((r) => setJobs(r.data.slice(0, 20)));
    }, [q]);

    const startForJob = async (job) => {
        setBusy(true);
        const oid = newClientOfflineId();
        const body = { job_id: job.id, work_type: workType, client_offline_id: oid };
        try {
            if (!online) throw new Error("offline");
            const { data } = await api.post("/v1/time-entries/start-timer", body);
            toast.success("Timer started");
            nav("/m/today");
        } catch (_) {
            await enqueue({ url: "/v1/time-entries/start-timer", method: "POST", body });
            refreshCount();
            toast.message("Queued offline");
            nav("/m/today");
        } finally { setBusy(false); }
    };

    const startForNew = async () => {
        if (!addr.trim()) return;
        setBusy(true);
        const oid = newClientOfflineId();
        const body = {
            job_id: null, work_type: workType,
            unallocated_address: addr.trim(),
            client_offline_id: oid,
        };
        try {
            if (!online) throw new Error("offline");
            await api.post("/v1/time-entries/start-timer", body);
            toast.success("Timer started at " + addr.trim());
        } catch (_) {
            await enqueue({ url: "/v1/time-entries/start-timer", method: "POST", body });
            refreshCount();
            toast.message("Queued offline");
        }
        setBusy(false);
        nav("/m/today");
    };

    return (
        <div data-testid={MOBILE.addRoot} className="px-5 pt-5 pb-32">
            <button onClick={() => nav("/m/today")} className="flex items-center gap-1 text-sm text-muted mb-4">
                <ArrowLeft size={16} /> Today
            </button>
            <h1 className="text-2xl font-extrabold tracking-[-0.03em]">Add job or address</h1>

            <div className="mt-5 grid grid-cols-2 gap-2 p-1 bg-[color:var(--surface-2)] rounded-xl">
                {["existing", "new"].map((t) => (
                    <button
                        key={t}
                        data-testid={`add-tab-${t}`}
                        onClick={() => setTab(t)}
                        className={
                            "py-2 rounded-lg text-sm font-medium transition-colors " +
                            (tab === t ? "bg-white text-ink shadow-sm" : "text-muted")
                        }
                    >
                        {t === "existing" ? "Existing jobs" : "New address"}
                    </button>
                ))}
            </div>

            <div className="eyebrow mt-6 mb-2">Work type</div>
            <div className="flex flex-wrap gap-2 mb-4">
                {WORK_TYPES.map((w) => (
                    <button
                        key={w}
                        onClick={() => setWorkType(w)}
                        className={"km-chip " + (w === workType ? "km-chip-navy-solid" : "km-chip-grey")}
                        style={{ cursor: "pointer" }}
                    >{w}</button>
                ))}
            </div>

            {tab === "existing" ? (
                <>
                    <div className="relative mb-3">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
                        <input
                            data-testid={MOBILE.searchJobs}
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search number, address, client…"
                            className="w-full h-11 pl-9 pr-3 rounded-xl bg-white border border-km text-sm"
                        />
                    </div>
                    <ul className="space-y-2">
                        {jobs.map((j) => (
                            <li key={j.id}>
                                <button
                                    onClick={() => startForJob(j)}
                                    disabled={busy}
                                    className="w-full km-card p-3 flex items-center gap-3 text-left"
                                >
                                    <div className="w-11 h-11 rounded-lg bg-[color:var(--km-navy-l)] text-[color:var(--km-navy)] flex items-center justify-center num font-bold text-sm">
                                        {j.job_number || "—"}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm text-ink truncate">{j.address}</div>
                                        <div className="text-xs text-muted truncate">{j.description}</div>
                                    </div>
                                    <Play size={16} className="text-[color:var(--km-green)]" fill="currentColor" />
                                </button>
                            </li>
                        ))}
                        {!jobs.length && <li className="text-sm text-muted text-center py-8">No jobs found</li>}
                    </ul>
                </>
            ) : (
                <>
                    <div className="rounded-xl p-3 mb-4 flex items-start gap-2"
                         style={{ background: "var(--km-yellow-l)", color: "var(--km-yellow-d)" }}>
                        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                        <div className="text-xs">
                            This shows up in Shannon&apos;s queue as <b>unallocated</b>. Nothing gets lost.
                        </div>
                    </div>
                    <label className="block text-xs uppercase tracking-[0.12em] text-muted mb-1.5">Address</label>
                    <input
                        data-testid={MOBILE.newAddress}
                        value={addr}
                        onChange={(e) => setAddr(e.target.value)}
                        placeholder="e.g. 24 Marlin Pl, Cleveland"
                        className="w-full h-11 px-3 rounded-xl bg-white border border-km text-sm"
                    />
                    <label className="block text-xs uppercase tracking-[0.12em] text-muted mt-4 mb-1.5">Reason (optional)</label>
                    <input
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Site meeting, ad hoc quote, etc."
                        className="w-full h-11 px-3 rounded-xl bg-white border border-km text-sm"
                    />
                    <button
                        data-testid={MOBILE.startHere}
                        onClick={startForNew}
                        disabled={busy || !addr.trim()}
                        className="mt-5 w-full h-14 rounded-2xl text-white font-bold flex items-center justify-center gap-2"
                        style={{ background: "linear-gradient(150deg,#268254,#14563A)" }}
                    >
                        <Play size={16} fill="currentColor" /> Start timer here
                    </button>
                </>
            )}
        </div>
    );
}
